import { useMemo, useState } from 'react';
import { Button, Chip, Dialog, Select, Switch, TextField } from '@nyce/admin-kit/ui';
import {
  ConfirmButton,
  EmptyState,
  ErrorBox,
  Loading,
  Page,
  TableScroller,
  Tabs,
  useAsync,
  useNotice,
} from '../../../components/common';
import { useTargets } from '../../../identity/session';
import { useBuddyWrite } from '../../api/client';
import {
  knowledgeFor,
  type Skill,
  type SkillInput,
  type SkillScope,
  type ValidationOutcome,
  type Validations,
} from '../../api/knowledge';
import { refOptions, toneOf, useLookup, useQueryState, when } from './shared';

const SCOPES: { value: SkillScope; label: string }[] = [
  { value: 'global', label: 'Global' },
  { value: 'tenant', label: 'Tenant' },
  { value: 'organization', label: 'Organization' },
  { value: 'system', label: 'System' },
];
const LOOKUP = { tenant: 'tenants', organization: 'organizations', system: 'systems' } as const;
const OUTCOMES: ValidationOutcome[] = ['passed', 'repaired', 'warned', 'errored'];
const DAYS = [7, 30, 90, 365];
type Tab = 'catalogue' | 'validations' | 'effective';

const blank = (): SkillInput => ({
  key: '',
  parentKey: '',
  scope: 'global',
  scopeId: '',
  name: '',
  description: '',
  body: '',
  always: false,
  enabled: true,
  sortOrder: 100,
  validator: '',
  validatorConfig: {},
});

const toInput = (s: Skill): SkillInput => ({
  key: s.key,
  parentKey: s.parentKey ?? '',
  scope: s.scope,
  scopeId: s.scopeId ?? '',
  name: s.name,
  description: s.description ?? '',
  body: s.body ?? '',
  always: s.always,
  enabled: s.enabled,
  sortOrder: s.sortOrder,
  validator: s.validator ?? '',
  validatorConfig: s.validatorConfig ?? {},
});

/** Why a skill cannot be saved yet (buddy3 checks the same and more). */
export function skillProblems(v: SkillInput): string[] {
  const p: string[] = [];
  if (!v.key) p.push('Key is required.');
  else if (!/^[A-Za-z0-9._/-]{1,96}$/.test(v.key))
    p.push('Key: letters, digits, - _ . and / only, at most 96 characters.');
  if (!v.name.trim()) p.push('Name is required.');
  if (v.scope !== 'global' && !v.scopeId.trim()) p.push(`Pick the ${v.scope}.`);
  if (v.scope === 'global' && !v.body?.trim())
    p.push('A global skill needs a body: nothing wider to inherit from.');
  if (v.parentKey && v.parentKey.toLowerCase() === v.key.toLowerCase())
    p.push('A skill cannot be a chapter of itself.');
  if (v.parentKey && v.always) p.push('A chapter cannot be always on: only its parent is offered up front.');
  return p;
}

/** Chapters sit under their parent rather than alphabetically elsewhere: a skill and its references are read together. */
export function orderSkills(all: Skill[]): Skill[] {
  const byOrder = (a: Skill, b: Skill) => a.sortOrder - b.sortOrder || a.key.localeCompare(b.key);
  const tops = all.filter((s) => !s.parentKey).sort(byOrder);
  const orphans = all.filter((s) => s.parentKey && !tops.some((t) => t.key === s.parentKey));
  return [...tops.flatMap((t) => [t, ...all.filter((s) => s.parentKey === t.key).sort(byOrder)]), ...orphans];
}

function SkillDialog({
  target,
  initial,
  id,
  readOnly,
  onSaved,
  onClose,
}: {
  target: string;
  initial: SkillInput;
  id?: string;
  /** Shown, never edited: a product skill, or no `ophub:buddy:admin`. */
  readOnly: false | 'system' | 'scope';
  onSaved: (s: Skill) => void;
  onClose: () => void;
}) {
  const [v, setV] = useState<SkillInput>(initial);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const api = knowledgeFor(target).skills;
  const validators = useAsync(() => api.validators(), [target]);
  const refs = useLookup(target, v.scope === 'global' ? null : LOOKUP[v.scope]);
  const problems = readOnly ? [] : skillProblems(v);
  const isOverride = v.scope !== 'global' && !v.body;
  const onFail = (v.validatorConfig?.onFail as string) ?? 'repair';
  const scopeOptions = refOptions(refs.data);
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const body = { ...v, body: v.body?.length ? v.body : null };
      onSaved(id ? await api.update(id, body) : await api.create(body));
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open
      onClose={onClose}
      title={!id ? 'New skill' : readOnly ? `Skill ${v.key}` : `Edit skill ${v.key}`}
      maxWidth={860}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {readOnly ? 'Close' : 'Cancel'}
          </Button>
          {!readOnly && (
            <Button onClick={() => void save()} loading={busy} disabled={problems.length > 0}>
              Save
            </Button>
          )}
        </>
      }
    >
      <ErrorBox error={error} />
      {readOnly && (
        <p className="nyk-card-intro">
          {readOnly === 'system'
            ? 'Ships with the product: its text lives in buddy3’s kb/skills. Switch it off per tenant, organization or system instead.'
            : 'Read-only: changing skills needs ophub:buddy:admin.'}
        </p>
      )}
      <div className="nyk-form" data-testid="skill-form">
        <TextField
          label="Key"
          value={v.key}
          disabled={!!readOnly}
          onChange={(e) => setV({ ...v, key: e.target.value.trim() })}
          hint="What the model types into load_skill, e.g. create-report. A chapter is <parent>/<name>."
        />
        <TextField
          label="Name"
          value={v.name}
          disabled={!!readOnly}
          onChange={(e) => setV({ ...v, name: e.target.value })}
        />
        <Select
          label="Scope"
          value={v.scope}
          options={SCOPES}
          disabled={!!readOnly}
          onChange={(scope) => setV({ ...v, scope: scope as SkillScope, scopeId: '' })}
        />
        {v.scope === 'global' ? (
          <TextField label="Applies to" value="Every system, unless a narrower level overrides it" disabled />
        ) : scopeOptions.length > 0 ? (
          <Select
            label={SCOPES.find((s) => s.value === v.scope)!.label}
            value={v.scopeId}
            placeholder={`Pick a ${v.scope}`}
            options={
              v.scopeId && !scopeOptions.some((o) => o.value === v.scopeId)
                ? [{ value: v.scopeId, label: v.scopeId }, ...scopeOptions]
                : scopeOptions
            }
            disabled={!!readOnly}
            onChange={(scopeId) => setV({ ...v, scopeId })}
          />
        ) : (
          <TextField
            label={`${SCOPES.find((s) => s.value === v.scope)!.label} id`}
            value={v.scopeId}
            disabled={!!readOnly}
            onChange={(e) => setV({ ...v, scopeId: e.target.value.trim() })}
          />
        )}
        <TextField
          fieldClassName="nyk-span2"
          label="Use it when"
          value={v.description}
          disabled={!!readOnly}
          multiline
          rows={2}
          onChange={(e) => setV({ ...v, description: e.target.value })}
          hint="The only line always in the prompt: say WHEN to use the skill, not what it contains."
        />
        <TextField
          fieldClassName="nyk-span2"
          label="Parent skill (empty for a skill of its own)"
          value={v.parentKey}
          disabled={!!readOnly}
          onChange={(e) => setV({ ...v, parentKey: e.target.value.trim() })}
          hint="A chapter is loadable by key but stays out of the index; its parent's text points at it."
        />
        <TextField
          fieldClassName="nyk-span2"
          className="bdk-body"
          label="Body (markdown, sent to the model verbatim)"
          value={v.body ?? ''}
          disabled={!!readOnly}
          multiline
          rows={12}
          onChange={(e) => setV({ ...v, body: e.target.value })}
          hint={
            v.scope === 'global'
              ? 'Required: there is no wider level to inherit text from.'
              : 'Empty keeps the wider level’s text and only changes the switches below.'
          }
        />
        <div className="nyk-ui-field nyk-switch-field">
          <span>Enabled</span>
          <Switch
            checked={v.enabled}
            disabled={!!readOnly}
            onChange={(enabled) => setV({ ...v, enabled })}
            label={
              v.enabled
                ? 'Offered to the agent'
                : isOverride
                  ? 'Switches the wider skill off here'
                  : 'Not offered'
            }
          />
        </div>
        <div className="nyk-ui-field nyk-switch-field">
          <span>Always on</span>
          <Switch
            checked={v.always}
            disabled={!!readOnly}
            onChange={(always) => setV({ ...v, always })}
            label={v.always ? 'Whole, in every prompt' : 'Behind load_skill (one index line)'}
          />
        </div>
        <TextField
          label="Order"
          type="number"
          value={String(v.sortOrder)}
          disabled={!!readOnly}
          onChange={(e) => setV({ ...v, sortOrder: Number(e.target.value) || 0 })}
        />
        <Select
          label="Validation rule"
          value={v.validator}
          options={[
            { value: '', label: 'None' },
            ...(validators.data ?? []).map((r) => ({ value: r.name, label: r.name })),
            ...(v.validator && !validators.data?.some((r) => r.name === v.validator)
              ? [{ value: v.validator, label: `${v.validator} (unknown to this build)` }]
              : []),
          ]}
          disabled={!!readOnly}
          onChange={(validator) => setV({ ...v, validator })}
        />
        {v.validator && (
          <>
            <Select
              label="When it fails"
              value={onFail}
              options={[
                { value: 'repair', label: 'Send the answer back once' },
                { value: 'warn', label: 'Warn only, never hold the answer' },
              ]}
              disabled={!!readOnly}
              onChange={(next) =>
                setV({ ...v, validatorConfig: { ...(v.validatorConfig ?? {}), onFail: next } })
              }
            />
            <p className="nyk-card-intro nyk-span2">
              {validators.data?.find((r) => r.name === v.validator)?.description} The rule runs on every turn
              that loaded this skill; a failure buys one correction round, a second failure delivers the
              answer with the finding on it.
            </p>
          </>
        )}
      </div>
      {problems.length > 0 && (
        <ul className="bdk-problems" data-testid="skill-problems">
          {problems.map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ul>
      )}
    </Dialog>
  );
}

function Catalogue({
  target,
  skills,
  canWrite,
  onOpen,
  onRemove,
}: {
  target: string;
  skills: Skill[];
  canWrite: boolean;
  onOpen: (s: Skill) => void;
  onRemove: (s: Skill) => void;
}) {
  const q = useQueryState();
  const text = q.get('q').toLowerCase();
  const scope = q.get('scope');
  const rows = useMemo(
    () =>
      orderSkills(skills).filter(
        (s) =>
          (!scope || s.scope === scope) &&
          (!text || [s.key, s.name, s.description, s.scopeId].some((x) => x?.toLowerCase().includes(text)))
      ),
    [skills, text, scope]
  );
  return (
    <>
      <div className="bd-toolbar bdk-toolbar">
        <input
          className="nyk-ui-input"
          type="search"
          aria-label="Filter skills"
          placeholder="Filter by key, name or scope id"
          value={q.get('q')}
          onChange={(e) => q.set({ q: e.target.value })}
        />
        <Select
          aria-label="Scope"
          value={scope}
          options={[{ value: '', label: 'Every scope' }, ...SCOPES]}
          onChange={(v) => q.set({ scope: v })}
        />
      </div>
      {rows.length === 0 ? (
        <EmptyState
          card
          title={skills.length ? 'No skill matches the filter' : 'No skills yet'}
          hint={
            skills.length
              ? undefined
              : 'Product skills are seeded when buddy3 starts; custom ones are written here.'
          }
        />
      ) : (
        <div className="nyk-card">
          <TableScroller>
            <table className="nyk-ui-table nyk-cards" data-testid="skills-table" data-target={target}>
              <thead>
                <tr>
                  <th>Skill</th>
                  <th>Scope</th>
                  <th>Status</th>
                  <th>Delivery</th>
                  <th>Rule</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((s) => (
                  <tr key={s.id} data-skill={s.key}>
                    <td className="nyk-card-title">
                      <b>
                        {s.parentKey ? '↳ ' : ''}
                        {s.name}
                      </b>{' '}
                      {s.origin === 'system' && (
                        <Chip size="sm" title="Ships with the product; edited in buddy3’s kb/skills">
                          system
                        </Chip>
                      )}
                      <div className="nyk-ui-hint nyk-mono">{s.key}</div>
                      {s.description && <div className="nyk-ui-hint bdk-clamp">{s.description}</div>}
                    </td>
                    <td data-label="Scope">
                      <Chip size="sm">{s.scope}</Chip>
                      {s.scopeId && <div className="nyk-ui-hint">{s.scopeId}</div>}
                    </td>
                    <td data-label="Status">
                      <Chip size="sm" className={s.enabled ? 'nyk-tone-green' : 'nyk-tone-grey'}>
                        {s.enabled ? 'enabled' : 'off here'}
                      </Chip>
                    </td>
                    <td data-label="Delivery">
                      {s.parentKey ? 'chapter' : s.always ? 'always in the prompt' : 'load_skill'}
                      {!s.body && <div className="nyk-ui-hint">inherits the text</div>}
                    </td>
                    <td data-label="Rule">
                      {s.validator ? (
                        <>
                          <Chip size="sm" className="nyk-tone-blue">
                            {s.validator}
                          </Chip>
                          {s.validatorConfig?.onFail === 'warn' && (
                            <div className="nyk-ui-hint">warn only</div>
                          )}
                        </>
                      ) : (
                        <span className="nyk-ui-hint">—</span>
                      )}
                    </td>
                    <td className="nyk-row-actions">
                      <Button size="sm" variant="ghost" onClick={() => onOpen(s)}>
                        {canWrite && s.origin !== 'system' ? 'Edit' : 'View'}
                      </Button>
                      {canWrite && s.origin !== 'system' && (
                        <ConfirmButton
                          size="sm"
                          variant="danger"
                          message={`Delete the skill ${s.key}?`}
                          onConfirm={() => onRemove(s)}
                        >
                          Delete
                        </ConfirmButton>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroller>
        </div>
      )}
    </>
  );
}

/** What the rules concluded: a skill that keeps being repaired needs better words, a rule that keeps erroring a fix. */
function ValidationsTab({ target, skills }: { target: string; skills: Skill[] }) {
  const q = useQueryState();
  const skill = q.get('skill');
  const system = q.get('system');
  const outcome = q.get('outcome') as ValidationOutcome | '';
  const days = DAYS.includes(Number(q.get('days'))) ? Number(q.get('days')) : 30;
  const systems = useLookup(target, 'systems');
  const data = useAsync<Validations>(
    () =>
      knowledgeFor(target).skills.validations({
        skill: skill || undefined,
        system: system || undefined,
        outcome: outcome || undefined,
        days,
        limit: 100,
      }),
    [target, skill, system, outcome, days]
  );
  const ruled = [...new Set(skills.filter((s) => s.validator).map((s) => s.key))].sort();
  const v = data.data;
  return (
    <>
      <div className="bd-toolbar bdk-toolbar" data-testid="validation-filters">
        <Select
          aria-label="Skill"
          value={skill}
          options={[
            { value: '', label: 'Every skill' },
            ...[...new Set([...ruled, ...(skill ? [skill] : [])])].map((k) => ({ value: k, label: k })),
          ]}
          onChange={(x) => q.set({ skill: x })}
        />
        <Select
          aria-label="System"
          value={system}
          options={[
            { value: '', label: 'Every system' },
            ...refOptions(systems.data),
            ...(system && !systems.data?.some((s) => s.id === system)
              ? [{ value: system, label: system }]
              : []),
          ]}
          onChange={(x) => q.set({ system: x })}
        />
        <Select
          aria-label="Outcome"
          value={outcome}
          options={[{ value: '', label: 'Every outcome' }, ...OUTCOMES.map((o) => ({ value: o, label: o }))]}
          onChange={(x) => q.set({ outcome: x })}
        />
        <Select
          aria-label="Days"
          value={String(days)}
          options={DAYS.map((d) => ({ value: String(d), label: `Last ${d} days` }))}
          onChange={(x) => q.set({ days: x === '30' ? null : x })}
        />
      </div>
      <ErrorBox error={data.error} />
      <Loading loading={data.loading && !v} />
      {v && v.stats.length === 0 && v.runs.length === 0 && (
        <EmptyState
          card
          title="Nothing validated"
          hint="A row appears whenever a turn uses a skill that carries a validation rule."
        />
      )}
      {v && v.stats.length > 0 && (
        <div className="nyk-card">
          <div className="nyk-card-head">
            <h2>Per skill</h2>
            <span className="nyk-ui-hint">
              Last {days} days{system ? ` · ${system}` : ''}
            </span>
          </div>
          <TableScroller>
            <table className="nyk-ui-table nyk-cards" data-testid="validation-stats">
              <thead>
                <tr>
                  <th>Skill</th>
                  <th>Rule</th>
                  <th className="nyk-ui-num">Runs</th>
                  <th className="nyk-ui-num">Passed</th>
                  <th className="nyk-ui-num">Repaired</th>
                  <th className="nyk-ui-num">Warned</th>
                  <th className="nyk-ui-num">Errored</th>
                  <th>Last</th>
                </tr>
              </thead>
              <tbody>
                {v.stats.map((s) => (
                  <tr key={`${s.skillKey}:${s.validator}`} data-stat={s.skillKey}>
                    <td className="nyk-card-title">
                      <button type="button" className="nyk-link" onClick={() => q.set({ skill: s.skillKey })}>
                        {s.skillKey}
                      </button>
                    </td>
                    <td data-label="Rule">{s.validator}</td>
                    <td className="nyk-ui-num" data-label="Runs">
                      {s.total}
                    </td>
                    <td className="nyk-ui-num" data-label="Passed">
                      {s.passed}
                    </td>
                    <td className="nyk-ui-num" data-label="Repaired">
                      {s.repaired}
                    </td>
                    <td className="nyk-ui-num" data-label="Warned">
                      {s.warned}
                    </td>
                    <td className={`nyk-ui-num${s.errored ? ' bdk-bad' : ''}`} data-label="Errored">
                      {s.errored}
                    </td>
                    <td data-label="Last">{when(s.lastAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroller>
        </div>
      )}
      {v && v.runs.length > 0 && (
        <div className="nyk-card">
          <div className="nyk-card-head">
            <h2>Runs</h2>
            <span className="nyk-ui-hint">Newest {v.runs.length}</span>
          </div>
          <TableScroller>
            <table className="nyk-ui-table nyk-cards" data-testid="validation-runs">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Skill</th>
                  <th>System</th>
                  <th>Outcome</th>
                  <th className="nyk-ui-num">Checked</th>
                  <th>Findings</th>
                </tr>
              </thead>
              <tbody>
                {v.runs.map((r) => (
                  <tr key={r.id} data-run={r.id}>
                    <td className="nyk-card-title">{when(r.createdAt)}</td>
                    <td data-label="Skill">
                      {r.skillKey}
                      <div className="nyk-ui-hint">{r.validator}</div>
                    </td>
                    <td data-label="System">
                      {r.systemId}
                      {r.userCode && <div className="nyk-ui-hint">{r.userCode}</div>}
                    </td>
                    <td data-label="Outcome">
                      <Chip size="sm" className={toneOf(r.outcome)}>
                        {r.outcome}
                      </Chip>
                      {r.gated && <div className="nyk-ui-hint">answer held back</div>}
                    </td>
                    <td className="nyk-ui-num" data-label="Checked">
                      {r.checked}
                      {r.failed > 0 && <div className="nyk-ui-hint">{r.failed} failed</div>}
                    </td>
                    <td data-label="Findings">
                      {r.findings.length === 0 ? (
                        <span className="nyk-ui-hint">
                          {r.outcome === 'errored' ? 'nothing to check' : '—'}
                        </span>
                      ) : (
                        r.findings.map((f, i) => (
                          <div key={i} className="bdk-finding">
                            <b>{f.artifact}</b> — {f.problem}
                            {f.detail && <div className="nyk-ui-hint">{f.detail}</div>}
                          </div>
                        ))
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroller>
        </div>
      )}
    </>
  );
}

/** What one system's agent ends up with once the cascade and the `skills` flag are applied. */
function EffectiveTab({ target }: { target: string }) {
  const q = useQueryState();
  const system = q.get('system');
  const systems = useLookup(target, 'systems');
  const data = useAsync(
    () => (system ? knowledgeFor(target).skills.effective(system) : Promise.resolve(null)),
    [target, system]
  );
  const e = data.data;
  return (
    <>
      <div className="bd-toolbar bdk-toolbar">
        <Select
          aria-label="System"
          value={system}
          placeholder="Pick a system"
          options={[
            ...refOptions(systems.data),
            ...(system && !systems.data?.some((s) => s.id === system)
              ? [{ value: system, label: system }]
              : []),
          ]}
          onChange={(x) => q.set({ system: x })}
        />
      </div>
      <ErrorBox error={data.error} />
      {!system && (
        <EmptyState card title="Pick a system" hint="Shows the skills its agent is offered right now." />
      )}
      <Loading loading={!!system && data.loading && !e} />
      {e && (
        <div className="nyk-card" data-testid="effective-skills">
          {!e.featureEnabled && (
            <p className="bdk-note nyk-tone-amber">
              The skills feature is off for {e.systemId}: its agent gets none of these.
            </p>
          )}
          {e.skills.length === 0 ? (
            <EmptyState title="No skills reach this system" />
          ) : (
            <TableScroller>
              <table className="nyk-ui-table nyk-cards">
                <thead>
                  <tr>
                    <th>Skill</th>
                    <th>Decided at</th>
                    <th>Delivery</th>
                    <th>Rule</th>
                  </tr>
                </thead>
                <tbody>
                  {orderSkills(e.skills).map((s) => (
                    <tr key={s.key} data-effective={s.key}>
                      <td className="nyk-card-title">
                        <b>
                          {s.parentKey ? '↳ ' : ''}
                          {s.name}
                        </b>
                        <div className="nyk-ui-hint nyk-mono">{s.key}</div>
                      </td>
                      <td data-label="Decided at">
                        <Chip size="sm">{s.scope}</Chip>
                        {s.scopeId && <div className="nyk-ui-hint">{s.scopeId}</div>}
                      </td>
                      <td data-label="Delivery">
                        {s.parentKey ? 'chapter' : s.always ? 'always in the prompt' : 'load_skill'}
                      </td>
                      <td data-label="Rule">{s.validator || <span className="nyk-ui-hint">—</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableScroller>
          )}
        </div>
      )}
    </>
  );
}

/** Skills: authored procedures the agent loads on demand (buddy3 docs/design/skills.md). */
export function BuddySkillsPage() {
  const { active } = useTargets();
  const canWrite = useBuddyWrite();
  const q = useQueryState();
  const tab = (['validations', 'effective'].includes(q.get('tab')) ? q.get('tab') : 'catalogue') as Tab;
  const skills = useAsync(() => knowledgeFor(active).skills.list(), [active]);
  const [editing, setEditing] = useState<{
    value: SkillInput;
    id?: string;
    readOnly: false | 'system' | 'scope';
  } | null>(null);
  const notice = useNotice();
  const remove = async (s: Skill) => {
    try {
      await knowledgeFor(active).skills.remove(s.id);
      skills.setData((prev) => (prev ?? []).filter((x) => x.id !== s.id));
      notice.success(`Deleted ${s.key}`);
    } catch (e) {
      notice.error((e as Error).message);
    }
  };
  return (
    <Page
      title="Skills"
      description="Written procedures the agent may load. The prompt carries one line per skill; the text arrives when the model asks. Cascade global → tenant → organization → system: a narrower row replaces the text or switches the skill off there."
      actions={
        canWrite && tab === 'catalogue' ? (
          <Button onClick={() => setEditing({ value: blank(), readOnly: false })}>New skill</Button>
        ) : undefined
      }
    >
      {notice.element}
      <Tabs
        value={tab}
        onChange={(t) => q.set({ tab: t === 'catalogue' ? null : t })}
        options={[
          { value: 'catalogue', label: 'Catalogue' },
          { value: 'validations', label: 'Validations' },
          { value: 'effective', label: 'Per system' },
        ]}
      />
      <ErrorBox error={skills.error} />
      {tab === 'catalogue' && (
        <>
          <Loading loading={skills.loading && !skills.data} />
          {skills.data && (
            <Catalogue
              target={active}
              skills={skills.data}
              canWrite={canWrite}
              onOpen={(s) =>
                setEditing({
                  value: toInput(s),
                  id: s.id,
                  readOnly: s.origin === 'system' ? 'system' : canWrite ? false : 'scope',
                })
              }
              onRemove={(s) => void remove(s)}
            />
          )}
        </>
      )}
      {tab === 'validations' && <ValidationsTab target={active} skills={skills.data ?? []} />}
      {tab === 'effective' && <EffectiveTab target={active} />}
      {editing && (
        <SkillDialog
          target={active}
          initial={editing.value}
          id={editing.id}
          readOnly={editing.readOnly}
          onSaved={(s) => {
            void skills.reload();
            notice.success(`Saved ${s.key}`);
          }}
          onClose={() => setEditing(null)}
        />
      )}
    </Page>
  );
}
